import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AxiosError } from 'axios';
import { AuthApi, AuthUser, tokenStorage } from '../services';

type LoginParams = {
  phone: string;
  password: string;
  remember: boolean;
};

type AuthContextValue = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (params: LoginParams) => Promise<AuthUser>;
  logout: () => void;
  refreshUser: () => Promise<AuthUser | null>;
  clearError: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const responseMessage = (error.response?.data as { error?: string; message?: string } | undefined)?.error;
    const fallbackMessage = (error.response?.data as { message?: string } | undefined)?.message;
    return responseMessage ?? fallbackMessage ?? error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initialize = useCallback(async () => {
    const token = tokenStorage.getAccessToken();

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const currentUser = await AuthApi.me();
      setUser(currentUser);
      setError(null);
    } catch (initError) {
      tokenStorage.clear();
      setUser(null);
      setError(parseErrorMessage(initError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const login = useCallback(async ({ phone, password, remember }: LoginParams) => {
    setLoading(true);

    try {
      const response = await AuthApi.login({ phone, password, remember });
      setUser(response.user);
      setError(null);
      return response.user;
    } catch (loginError) {
      const message = parseErrorMessage(loginError);
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    AuthApi.logout();
    setUser(null);
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await AuthApi.me();
      setUser(currentUser);
      setError(null);
      return currentUser;
    } catch (refreshError) {
      const message = parseErrorMessage(refreshError);
      setError(message);
      setUser(null);
      return null;
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      loading,
      error,
      login,
      logout,
      refreshUser,
      clearError,
    }),
    [user, loading, error, login, logout, refreshUser, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

